import React, { useState, useEffect } from 'react';
import { Button, Dialog } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import { entityNameMinimumLength, validateAlphaNumeric, validateEntityName } from 'const/regex';
import { Store, Brand, User } from '@restore/services-lib';
import {
  StoreInterface,
  UpdateStoreInterface,
  useStoresContext,
} from 'pages/Stores/stores.context';
import { FormFieldInterface } from 'components/Form/Form.types';
import Form from 'components/Form/Form';
import CustomIcon from 'components/Icon/CustomIcon';
import MultiselectDropdown from 'components/DropdownSelect/MultiSelectDropdown/MultiselectDropdown';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { useBrandsContext } from 'pages/Brands/brands.context';
import { useUsersContext } from 'context/users.context';
import TagMultiSelectDropdown from 'components/DropdownSelect/MultiSelectDropdown/Usage/TagsMultiSelectDropdown';
import { hasPermissions } from 'config/ability';
import { useAuthContext } from 'context/auth.context';

const getUpdatedStore = (store: StoreInterface) => ({
  id: store.id,
  storeNumber: store.storeNumber,
  name: store.name,
  // address: store.address,
  state: store.state,
  timezone: store.timezone,
  latlng: store.latlng,
  brandIds: store.brands.map((brand) => brand.id),
  userIds: store.users.map((user) => user.id),
  storeManagerUserIds: store.storeManagerUsers?.map((user) => user.id) || [],
  tagIds: store.tags.map((tag) => tag.id),
});

const getSelectedBrands = (store: StoreInterface) =>
  store.brands.map((brand: Brand) => ({ id: brand.id, name: brand.name }));

const getSelectedUsers = (store: StoreInterface) =>
  store.users.map((user: User) => ({ id: user.id, name: `${user.firstName} ${user.lastName}` }));

const getSelectedStoreManagerUsers = (store: StoreInterface) =>
  store.storeManagerUsers.map((user: User) => ({
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
  }));

const UpdateStore = ({
  store,
  text,
  showIcon = false,
}: {
  store: StoreInterface;
  text?: string;
  showIcon?: boolean;
}) => {
  const [show, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { editStore } = useStoresContext();
  const { brands } = useBrandsContext();
  const { users } = useUsersContext();
  const { ability, userTenantId } = useAuthContext();
  const [updatedStore, setUpdatedStore] = useState<UpdateStoreInterface>(getUpdatedStore(store));
  const [selectedBrands, setSelectedBrands] = useState<DropdownOption[]>(getSelectedBrands(store));
  const [selectedUsers, setSelectedUsers] = useState<DropdownOption[]>(getSelectedUsers(store));
  const [selectedStoreManagerUsers, setSelectedStoreManagerUsers] = useState<DropdownOption[]>(
    getSelectedStoreManagerUsers(store)
  );

  const brandsOptions: DropdownOption[] = brands.map((brand) => ({
    name: brand.name,
    id: brand.id,
  }));

  const usersOptions: DropdownOption[] = users.map((user) => ({
    name: `${user.firstName} ${user.lastName}`,
    id: user.id,
  }));

  useEffect(() => {
    setUpdatedStore(getUpdatedStore(store));
  }, [store]);

  // Return null if user does not have 'Update' permissions
  if (
    !hasPermissions(ability, 'update', 'Store', { tenantId: userTenantId }) &&
    !hasPermissions(ability, 'update', 'Store', { storeId: store.id })
  ) {
    return null;
  }

  const handleUpdateStore = async () => {
    setLoading(true);
    await editStore(updatedStore);
    setShow(false);
    setLoading(false);
  };

  const handleUpdateField = (value: string | number, key: keyof Store) =>
    setUpdatedStore({ ...updatedStore, [key]: value });

  const handleUpdateStoreBrands = (updateBrands: DropdownOption[]) => {
    setSelectedBrands(updateBrands);
    setUpdatedStore({
      ...updatedStore,
      brandIds: updateBrands.map((brand) => Number(brand.id)),
    });
  };

  const handleUpdateStoreUsers = (updateUsers: DropdownOption[]) => {
    setSelectedUsers(updateUsers);
    setUpdatedStore({
      ...updatedStore,
      userIds: updateUsers.map((user) => Number(user.id)),
    });
  };

  const handleUpdateStoreManagerUsers = (updateUsers: DropdownOption[]) => {
    setSelectedStoreManagerUsers(updateUsers);
    setUpdatedStore({
      ...updatedStore,
      storeManagerUserIds: updateUsers.map((user) => Number(user.id)),
    });
  };

  const handleUpdateTags = (updateTags: DropdownOption[]) => {
    setUpdatedStore({
      ...updatedStore,
      tagIds: updateTags.map((tag) => Number(tag.id)),
    });
  };

  const fields: FormFieldInterface[] = [
    {
      label: 'Store Number',
      value: String(updatedStore.storeNumber),
      onChange: (val) => handleUpdateField(val, 'storeNumber'),
      fullWidth: true,
      validator: validateAlphaNumeric,
    },
    {
      label: 'Store Name',
      value: updatedStore.name,
      onChange: (val) => handleUpdateField(val, 'name'),
      fullWidth: true,
      requiredMinimumLength: entityNameMinimumLength,
      validator: validateEntityName,
    },
    // {
    //   label: 'Address',
    //   value: updatedStore.address,
    //   onChange: (val: string) => handleUpdateField(val, 'address'),
    //   fullWidth: true,
    // },
    {
      label: 'State / Province',
      value: updatedStore.state,
      onChange: (val: string) => handleUpdateField(val, 'state'),
      fullWidth: true,
    },
    {
      label: 'Timezone',
      value: updatedStore.timezone,
      onChange: (val: string) => handleUpdateField(val, 'timezone'),
      fullWidth: true,
    },
    {
      label: 'Lat/Lng',
      value: updatedStore.latlng,
      onChange: (val: string) => handleUpdateField(val, 'latlng'),
      fullWidth: true,
    },
    {
      label: 'Brands',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Brands'
          allOptions={brandsOptions}
          fullWidth={true}
          selected={selectedBrands}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateStoreBrands(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Users',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Users'
          allOptions={usersOptions}
          fullWidth={true}
          selected={selectedUsers}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateStoreUsers(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Store Manager(s)',
      onChange: () => {},
      customField: (
        <MultiselectDropdown
          fieldLabel='Store Manager(s)'
          allOptions={usersOptions}
          fullWidth={true}
          selected={selectedStoreManagerUsers}
          setSelected={(selectedOptions: DropdownOption[]) =>
            handleUpdateStoreManagerUsers(selectedOptions)
          }
        />
      ),
    },
    {
      label: 'Tags',
      onChange: () => {},
      customField: <TagMultiSelectDropdown selectedTags={store.tags} onChange={handleUpdateTags} />,
    },
  ];

  return (
    <>
      {showIcon ? (
        <CustomIcon onClick={() => setShow(true)} label={text} icon={<Edit />} />
      ) : (
        <Button onClick={() => setShow(true)} sx={{ marginRight: 1.125 }}>
          {text}
        </Button>
      )}
      <Dialog open={show} onClose={() => setShow(false)}>
        <Form
          fields={fields}
          title='Update Store'
          onSubmit={handleUpdateStore}
          submitting={loading}
          buttonText='Update Store'
        />
      </Dialog>
    </>
  );
};

export default UpdateStore;
